'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// UI lib components
import { LoadingOverlay } from '@mantine/core';

// Layouts
import WorkspaceLayout from '@layouts/WorkspaceLayout';
import GateLayout from '@layouts/GateLayout';

// Helpers
import { useVerifyAuth } from '@helpers/hooks/useVerifyAuth';

// Critical pages - loaded immediately (most frequently accessed)
import PageHome from '@features/Account/PageHome';
import PageResources from '@features/Resource/PageResources';
import PageNotFound from '@shared/static/PageNotFound';

// Prefetch secondary routes during idle time
function prefetchRoutes() {
  const routes = [
    () => import('@features/Resource/PageFile'),
    () => import('@features/Resource/PageDownloadConfirm'),
    () => import('@features/Group/PageGroups'),
    () => import('@features/User/PageUsers'),
    () => import('@features/Support/PageSupport'),
    () => import('@features/Workspace/PageSettings'),
    () => import('@features/Search/PageSearch'),
    () => import('@features/Redaction/PageRedactions'),
    () => import('@features/Analytics/PageAnalytics'),
    () => import('@features/AIAssistant/PageAIAssistant'),
    () => import('@features/DueDiligence/PageDueDiligence'),
    () => import('@features/RequestList/PageRequestList'),
  ];

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => {
      routes.forEach((route) => route());
    });
  } else {
    setTimeout(() => {
      routes.forEach((route) => route());
    }, 2000);
  }
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function WorkspaceRouter() {
  /* ********************************** HOOKS ********************************* */

  // Load all translations here to avoid flashes in the app
  useTranslation([
    'Common',
    'Support',
    'Resource',
    'Search',
    'Account',
    'Activity',
    'AIAssistant',
    'Analytics',
    'GateLayout',
    'Group',
    'Insight',
    'Notification',
    'Permission',
    'Redaction',
    'Settings',
    'Translation',
    'User',
    'Workspace',
    'WorkspaceLayout',
    'Referral',
    'DueDiligence',
    'RequestList',
  ]);

  // Prefetch secondary routes during browser idle time
  useEffect(() => {
    prefetchRoutes();
  }, []);

  const { verifyAuth } = useVerifyAuth();
  /* ******************************** RENDERING ******************************* */

  const getRouter = () =>
    createBrowserRouter([
      {
        path: '',
        element: <GateLayout />,
        children: [
          { path: '', element: <Navigate to="/home" /> },
          { path: 'home', element: <PageHome /> },
          // Routes publiques pour Stripe
          {
            path: 'stripe/success',
            lazy: () =>
              import('@features/Payment/PagePaymentSuccess').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'stripe/cancel',
            lazy: () =>
              import('@features/Payment/PagePaymentCancel').then((m) => ({
                Component: m.default,
              })),
          },
        ],
        errorElement: <PageNotFound />,
      },
      {
        path: 'workspace/:workspaceId',
        element: <WorkspaceLayout />,
        loader: verifyAuth,
        children: [
          { path: '', element: <Navigate to="resources" /> },
          {
            path: 'resources/:scope?/:folderId?',
            element: <PageResources />,
          },
          {
            path: 'file/:fileId',
            lazy: () =>
              import('@features/Resource/PageFile').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'download-confirm',
            lazy: () =>
              import('@features/Resource/PageDownloadConfirm').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'permissions',
            lazy: () =>
              import('@features/Group/PageGroups').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'users',
            lazy: () =>
              import('@features/User/PageUsers').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'redactions',
            lazy: () =>
              import('@features/Redaction/PageRedactions').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'analytics',
            lazy: () =>
              import('@features/Analytics/PageAnalytics').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'AIAssistant',
            lazy: () =>
              import('@features/AIAssistant/PageAIAssistant').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'support',
            lazy: () =>
              import('@features/Support/PageSupport').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'settings',
            lazy: () =>
              import('@features/Workspace/PageSettings').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'offline',
            lazy: () =>
              import('@features/Workspace/PageWorkspaceOffline').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'suspended',
            lazy: () =>
              import('@features/Workspace/PageWorkspaceUserSuspended').then(
                (m) => ({
                  Component: m.default,
                }),
              ),
          },
          {
            path: 'search',
            lazy: () =>
              import('@features/Search/PageSearch').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'referral',
            lazy: () =>
              import('@app/features/Referral/PageReferral').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'due-diligence',
            lazy: () =>
              import('@features/DueDiligence/PageDueDiligence').then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: 'request-list',
            lazy: () =>
              import('@features/RequestList/PageRequestList').then((m) => ({
                Component: m.default,
              })),
          },
        ],
      },
    ]);

  return (
    <RouterProvider
      router={getRouter()}
      fallbackElement={
        <LoadingOverlay
          h="100vh"
          w="100vw"
          overlayProps={{ blur: 3 }}
          visible
        />
      }
    />
  );
}

export default WorkspaceRouter;
